import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { changePassword } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<ChangePasswordForm>();

  const newPassword = watch('newPassword');

  const onSubmit = async (data: ChangePasswordForm) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('كلمات المرور الجديدة غير متطابقة');
      return;
    }

    if (data.currentPassword === data.newPassword) {
      toast.error('كلمة المرور الجديدة يجب أن تكون مختلفة عن الحالية');
      return;
    }

    setLoading(true);
    try {
      const result = await changePassword(
        data.currentPassword,
        data.newPassword
      );

      if (result.success) {
        toast.success('تم تغيير كلمة المرور بنجاح');
        reset();
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'فشل في تغيير كلمة المرور');
      }
    } catch (error) {
      toast.error('حدث خطأ غير متوقع');
      console.error('Change password error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className='min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'
    >
      <div className='max-w-md w-full space-y-8'>
        {/* Header */}
        <div className='text-center'>
          <div className='mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100'>
            <KeyIcon className='h-6 w-6 text-primary-600' />
          </div>
          <h2 className='mt-6 text-3xl font-extrabold text-gray-900'>
            تغيير كلمة المرور
          </h2>
          <p className='mt-2 text-sm text-gray-600'>
            {user?.name} - {user?.email}
          </p>
        </div>

        {/* Form */}
        <form className='mt-8 space-y-6' onSubmit={handleSubmit(onSubmit)}>
          <div className='space-y-4'>
            {/* Current Password */}
            <div>
              <label
                htmlFor='currentPassword'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                كلمة المرور الحالية
              </label>
              <div className='relative'>
                <input
                  {...register('currentPassword', {
                    required: 'كلمة المرور الحالية مطلوبة',
                  })}
                  type={showCurrentPassword ? 'text' : 'password'}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500'
                  placeholder='أدخل كلمة المرور الحالية'
                />
                <button
                  type='button'
                  className='absolute inset-y-0 left-0 pr-3 flex items-center'
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeSlashIcon className='h-5 w-5 text-gray-400' />
                  ) : (
                    <EyeIcon className='h-5 w-5 text-gray-400' />
                  )}
                </button>
              </div>
              {errors.currentPassword && (
                <p className='mt-1 text-sm text-red-600'>
                  {errors.currentPassword.message}
                </p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label
                htmlFor='newPassword'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                كلمة المرور الجديدة
              </label>
              <div className='relative'>
                <input
                  {...register('newPassword', {
                    required: 'كلمة المرور الجديدة مطلوبة',
                    minLength: {
                      value: 6,
                      message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message:
                        'كلمة المرور يجب أن تحتوي على حرف كبير وصغير ورقم',
                    },
                  })}
                  type={showNewPassword ? 'text' : 'password'}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500'
                  placeholder='أدخل كلمة المرور الجديدة'
                />
                <button
                  type='button'
                  className='absolute inset-y-0 left-0 pr-3 flex items-center'
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className='h-5 w-5 text-gray-400' />
                  ) : (
                    <EyeIcon className='h-5 w-5 text-gray-400' />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className='mt-1 text-sm text-red-600'>
                  {errors.newPassword.message}
                </p>
              )}

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className='mt-2'>
                  <div className='text-xs text-gray-500 mb-1'>
                    قوة كلمة المرور:
                  </div>
                  <div className='flex space-x-1 space-x-reverse'>
                    <div
                      className={`h-1 w-1/4 rounded ${
                        newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                    <div
                      className={`h-1 w-1/4 rounded ${
                        /[a-z]/.test(newPassword)
                          ? 'bg-green-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <div
                      className={`h-1 w-1/4 rounded ${
                        /[A-Z]/.test(newPassword)
                          ? 'bg-green-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <div
                      className={`h-1 w-1/4 rounded ${
                        /\d/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor='confirmPassword'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                تأكيد كلمة المرور الجديدة
              </label>
              <div className='relative'>
                <input
                  {...register('confirmPassword', {
                    required: 'تأكيد كلمة المرور مطلوب',
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500'
                  placeholder='أعد إدخال كلمة المرور الجديدة'
                />
                <button
                  type='button'
                  className='absolute inset-y-0 left-0 pr-3 flex items-center'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className='h-5 w-5 text-gray-400' />
                  ) : (
                    <EyeIcon className='h-5 w-5 text-gray-400' />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className='mt-1 text-sm text-red-600'>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          {/* Security Tips */}
          <div className='bg-blue-50 border border-blue-200 rounded-md p-4'>
            <h3 className='text-sm font-medium text-blue-900 mb-2'>
              نصائح الأمان:
            </h3>
            <ul className='text-xs text-blue-700 space-y-1'>
              <li>• استخدم كلمة مرور قوية تحتوي على 6 أحرف على الأقل</li>
              <li>• امزج بين الأحرف الكبيرة والصغيرة والأرقام</li>
              <li>• تجنب استخدام معلومات شخصية في كلمة المرور</li>
              <li>• لا تشارك كلمة المرور مع أحد</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className='flex flex-col space-y-3'>
            <button
              type='submit'
              disabled={loading}
              className='w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {loading ? <LoadingSpinner size='small' /> : 'تغيير كلمة المرور'}
            </button>

            <Link
              to='/dashboard'
              className='w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
            >
              <ArrowRightIcon className='h-4 w-4 ml-2' />
              العودة إلى لوحة التحكم
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
